"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {useAccount, useWorkspaceData} from "./workspace-context";
import {conversationIdValid, summarizeConversation, type CoreConversation, type CoreConversationList, type CoreConversationSummary, type CoreConversationMessage} from "@/lib/core-conversations";
import { apiFetch } from "@/lib/client-api";

import {readSSE,type CoreResult as CoreResponse,type CoreStreamEvent} from "@/lib/core-stream";
export type { CoreReference } from "@/lib/core-stream";
export type CoreMessage = CoreConversationMessage;

async function conversationRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await apiFetch(path, {...init, cache: "no-store", signal: init?.signal ? AbortSignal.any([init.signal, AbortSignal.timeout(30_000)]) : AbortSignal.timeout(30_000)});
  const body = await response.json().catch(() => ({})) as T & {error?: string};
  if (!response.ok) throw new Error(body.error || (response.status === 401 || response.status === 403
    ? "Sign in again to load your conversations." : "Conversation history is unavailable. Please try again."));
  return body as T;
}

/** Chats live in the authenticated store; this tab holds only the current streamed view. */
export function useCoreConversation(active: boolean) {
  const {document:workspace}=useWorkspaceData();
  const account = useAccount();
  const sendLock=useRef(false);
  const currentConversation = useRef<CoreConversation | null>(null);
  const [conversation, setConversation] = useState<CoreConversation | null>(null);
  const [history, setHistory] = useState<CoreConversationSummary[]>([]);
  const [historyQuery, setHistoryQuery] = useState("");
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [conversationError, setConversationError] = useState<string | null>(null);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [historyBusyId, setHistoryBusyId] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const listVersion = useRef(0);
  const historyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectionVersion = useRef(0);
  const initialized = useRef(false);
  const [ready, setReady] = useState(false);
  const [stopping, setStopping] = useState(false);
  const retrySelection = useRef<string | null>(null);
  const [messages, setMessages] = useState<CoreMessage[]>([]);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [localStreaming, setLocalStreaming] = useState(false);
  const [recording, setRecording] = useState(false);
  const [inputError, setInputError] = useState<string | null>(null);
  const controller = useRef<AbortController | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const recordingGeneration = useRef(0);
  const generation = useRef(0);

  const selectionKey = `asymmetric-core-selection:${account.id}:${typeof window === "undefined" ? "" : window.__ASYMMETRIC_ENVIRONMENT__ || "local"}`;
  const remember = useCallback((id: string | null, push = false) => {
    try { if (id) sessionStorage.setItem(selectionKey, id); else sessionStorage.removeItem(selectionKey); } catch { /* History remains durable when browser storage is disabled. */ }
    const url = new URL(window.location.href);
    if (id) url.searchParams.set("chat", id); else url.searchParams.delete("chat");
    if (url.href !== window.location.href) window.history[push ? "pushState" : "replaceState"](null, "", url);
  }, [selectionKey]);

  const applyConversation = useCallback((doc: CoreConversation) => {
    if (currentConversation.current?.id === doc.id && currentConversation.current.version > doc.version) return;
    currentConversation.current = doc;
    setConversation(doc);
    setMessages(doc.messages);
    setConversationError(null);
    retrySelection.current = null;
    if (!controller.current) setSending(Boolean(doc.activeTurnId));
  }, []);

  const refreshHistory = useCallback(async (cursor?: string) => {
    if (historyTimer.current) { clearTimeout(historyTimer.current); historyTimer.current = null; }
    const version = ++listVersion.current;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const params = new URLSearchParams({limit: "30", q: historyQuery});
      if (cursor) params.set("cursor", cursor);
      const result = await conversationRequest<CoreConversationList>(`/api/conversations?${params}`);
      if (version !== listVersion.current) return;
      setHistory(current => cursor ? [...current, ...result.conversations.filter(item => !current.some(row => row.id === item.id))] : result.conversations);
      setNextCursor(result.nextCursor);
    } catch (error) {
      if (version === listVersion.current) setHistoryError(error instanceof Error ? error.message : "Your history could not load.");
    } finally { if (version === listVersion.current) setHistoryLoading(false); }
  }, [historyQuery]);

  const open = useCallback(async (id: string, push = true, preserveComposer = false) => {
    if (sendLock.current || controller.current) return false;
    const version = ++selectionVersion.current;
    setLoadingConversation(true);
    setConversationError(null);
    retrySelection.current = id;
    try {
      const doc = await conversationRequest<CoreConversation>(`/api/conversations/${encodeURIComponent(id)}`);
      if (version !== selectionVersion.current) return false;
      applyConversation(doc);
      remember(doc.id, push);
      if (!preserveComposer) { setAttachment(null); setInputError(null); }
      return true;
    } catch (error) {
      if (version === selectionVersion.current) setConversationError(error instanceof Error ? error.message : "This conversation could not load.");
      return false;
    } finally { if (version === selectionVersion.current) setLoadingConversation(false); }
  }, [applyConversation, remember]);

  useEffect(() => {
    if (!active) return;
    historyTimer.current = setTimeout(() => { historyTimer.current = null; void refreshHistory(); }, 200);
    return () => { if (historyTimer.current) clearTimeout(historyTimer.current); historyTimer.current = null; listVersion.current += 1; };
  }, [active, refreshHistory]);

  useEffect(() => {
    if (!active || initialized.current) return;
    initialized.current = true;
    const id = new URL(window.location.href).searchParams.get("chat") || (() => { try { return sessionStorage.getItem(selectionKey); } catch { return null; } })();
    if (conversationIdValid(id)) void open(id, false).finally(() => setReady(true));
    else setReady(true);
  }, [active, open, selectionKey]);

  useEffect(() => {
    if (!active) return;
    const onFocus = () => { void refreshHistory(); if (!controller.current && !sendLock.current && currentConversation.current) void open(currentConversation.current.id, false, true); };
    const onPop = () => {
      const id = new URL(window.location.href).searchParams.get("chat");
      if (conversationIdValid(id)) void open(id, false);
      else if (!controller.current && !sendLock.current) { selectionVersion.current += 1; currentConversation.current = null; setConversation(null); setMessages([]); setSending(false); remember(null); }
    };
    window.addEventListener("focus", onFocus);
    window.addEventListener("popstate", onPop);
    return () => { window.removeEventListener("focus", onFocus); window.removeEventListener("popstate", onPop); };
  }, [active, open, refreshHistory, remember]);

  useEffect(() => {
    if (!active || !conversation?.activeTurnId || localStreaming || stopping) return;
    let live = true, inFlight = false;
    const timer = setInterval(() => {
      if (!live || inFlight || controller.current || sendLock.current) return;
      inFlight = true;
      const id = conversation.id, version = selectionVersion.current;
      void conversationRequest<CoreConversation>(`/api/conversations/${id}`).then(doc => {
        if (live && version === selectionVersion.current && !controller.current) applyConversation(doc);
      }).catch(error => { if (live) setConversationError(error instanceof Error ? error.message : "Unable to refresh this response."); }).finally(() => { inFlight = false; });
    }, 2000);
    return () => { live = false; clearInterval(timer); };
  }, [active, conversation?.id, conversation?.activeTurnId, applyConversation, stopping, localStreaming]);

  async function rename(id: string, title: string) {
    setHistoryBusyId(id);
    try {
      const doc = await conversationRequest<CoreConversation>(`/api/conversations/${id}`, {method: "PATCH", headers: {"content-type": "application/json"}, body: JSON.stringify({title})});
      setHistory(current => current.map(item => item.id === id ? summarizeConversation(doc) : item));
      if (currentConversation.current?.id === id) { currentConversation.current = doc; setConversation(doc); }
      return true;
    } catch (error) { setHistoryError(error instanceof Error ? error.message : "The title could not be saved."); return false; }
    finally { setHistoryBusyId(null); }
  }

  async function remove(id: string) {
    setHistoryBusyId(id);
    try {
      await conversationRequest(`/api/conversations/${id}`, {method: "DELETE"});
      setHistory(current => current.filter(item => item.id !== id));
      if (currentConversation.current?.id === id) clear();
      return true;
    } catch (error) { setHistoryError(error instanceof Error ? error.message : "The conversation could not be deleted."); return false; }
    finally { setHistoryBusyId(null); }
  }

  const stopRecording = useCallback(() => {
    if (recorder.current?.state === "recording") recorder.current.stop();
    stream.current?.getTracks().forEach(track => track.stop());
    stream.current = null;
    setRecording(false);
  }, []);

  useEffect(() => {
    if (!active) {
      recordingGeneration.current += 1;
      stopRecording();
    }
  }, [active, stopRecording]);

  useEffect(() => () => {
    // Development StrictMode replays setup after this cleanup. Let that setup
    // reopen the saved selection instead of discarding its only pending read.
    initialized.current = false;
    generation.current += 1;
    selectionVersion.current += 1;
    listVersion.current += 1;
    recordingGeneration.current += 1;
    controller.current?.abort();
    stream.current?.getTracks().forEach(track => track.stop());
  }, []);

  function attach(file: File | null) {
    setInputError(null);
    if (file && file.size > 12 * 1024 * 1024) {
      setInputError("Choose a file smaller than 12 MB.");
      return;
    }
    setAttachment(file);
  }

  async function toggleRecording() {
    if (recording) { stopRecording(); return; }
    const version = ++recordingGeneration.current;
    setInputError(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") throw new Error("unavailable");
      const media = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (version !== recordingGeneration.current) { media.getTracks().forEach(track => track.stop()); return; }
      const next = new MediaRecorder(media);
      stream.current = media;
      recorder.current = next;
      const chunks: Blob[] = [];
      next.addEventListener("dataavailable", event => { if (event.data.size) chunks.push(event.data); });
      next.addEventListener("stop", () => {
        media.getTracks().forEach(track => track.stop());
        if (version !== recordingGeneration.current) return;
        const type = next.mimeType || "audio/webm";
        const extension = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
        attach(new File(chunks, `voice-note.${extension}`, { type }));
        stream.current = null;
        setRecording(false);
      });
      next.start();
      setRecording(true);
    } catch {
      setInputError("Microphone access is unavailable. You can attach an audio file instead.");
    }
  }

  function clear() {
    generation.current += 1;
    recordingGeneration.current += 1;
    stopRecording();
    controller.current?.abort();
    controller.current = null;
    selectionVersion.current += 1;
    currentConversation.current = null;
    setConversation(null);
    setConversationError(null);
    setLoadingConversation(false);
    setStopping(false);
    retrySelection.current = null;
    remember(null, true);
    setMessages([]);
    setAttachment(null);
    setInputError(null);
    setSending(false);
    setLocalStreaming(false);
    sendLock.current=false;
  }

  async function stop() {
    if (stopping) return;
    const doc = currentConversation.current;
    const turnId = doc?.activeTurnId || messages.findLast(item => item.pending)?.id;
    if (!doc || !turnId) {
      generation.current += 1;
      controller.current?.abort(); controller.current = null;
      setMessages(current => current.map(item => item.pending ? {...item, pending: false, stopped: true, completedAt: Date.now()} : item));
      setSending(false); setLocalStreaming(false); sendLock.current = false;
      return;
    }
    setStopping(true);
    generation.current += 1;
    controller.current?.abort();
    controller.current = null;
    try {
      const saved = await conversationRequest<CoreConversation>(`/api/conversations/${doc.id}`, {method: "PATCH", headers: {"content-type": "application/json"}, body: JSON.stringify({action: "stop", turnId})});
      applyConversation(saved);
      void refreshHistory();
    } catch (error) {
      setConversationError(error instanceof Error ? error.message : "The stop could not be saved. Try again.");
      // Keep Stop available until the server confirms the turn is no longer running.
    } finally { sendLock.current = false; setStopping(false); setLocalStreaming(false); }
  }

  async function send(message: string, context = ""): Promise<CoreResponse | null> {
    if (sendLock.current || sending || loadingConversation || !ready || (!message.trim() && !attachment)) return null;
    sendLock.current=true;
    const id = crypto.randomUUID();
    const version = ++generation.current;
    const file = attachment;
    setMessages(current => [...current,
      { id: `${id}-user`, role: "user", text: message.trim() || "Analyze this attachment.", meta: file?.name },
      { id, role: "assistant", text: "", pending: true, startedAt: Date.now(), activities: [{id:"connect",label:"Connecting to Core",state:"running"}] },
    ]);
    setSending(true);
    setLocalStreaming(true);
    setInputError(null);
    const form = new FormData();
    form.set("stream","1");
    form.set("provider",workspace.providers.research);
    form.set("model",workspace.providers.model);
    form.set("message", message);
    if (context) form.set("context", context);
    if (file) form.set("attachment", file);
    const abort = new AbortController();
    controller.current = abort;
    try {
      let doc = currentConversation.current;
      if (!doc) {
        const conversationId = crypto.randomUUID();
        doc = await conversationRequest<CoreConversation>("/api/conversations", {method: "POST", headers: {"content-type": "application/json"}, body: JSON.stringify({id: conversationId, title: (message.trim() || file?.name || "New chat").replace(/\s+/g, " ").slice(0, 120)}), signal: abort.signal});
        if (version !== generation.current) return null;
        currentConversation.current = doc;
        setConversation(doc);
        remember(doc.id, true);
      }
      currentConversation.current = {...doc, activeTurnId: id};
      setConversation(currentConversation.current);
      form.set("conversationId", doc.id);
      form.set("turnId", id);
      const response = await apiFetch("/api/agent", { method: "POST", body: form, signal: abort.signal });
      let payload: CoreResponse | undefined;
      if(response.headers.get("content-type")?.includes("text/event-stream") && response.ok && response.body){
        for await (const data of readSSE(response.body,abort.signal)){
          if(version!==generation.current)return null;
          if(!data || typeof data!=="object" || !("type" in data))throw new Error("Core returned an unreadable stream. Please try again.");
          const event=data as CoreStreamEvent;
          if(event.type==="error")throw new Error(event.message);
          if(event.type==="complete"){payload=event.result;break;}
          setMessages(current=>current.map(item=>{
            if(item.id!==id)return item;
            if(event.type==="text")return {...item,text:item.text+event.delta};
            if(event.type==="references")return {...item,references:event.references};
            if(event.type==="reasoning-summary")return {...item,summaries:{...item.summaries,[event.id]:(item.summaries?.[event.id]||"")+event.delta}};
            if(event.type==="activity")return {...item,activities:[...(item.activities||[]).filter(a=>a.id!==event.activity.id&&a.id!=="connect"),event.activity]};
            return item;
          }));
        }
        if(!payload)throw new Error("The connection ended before Core finished. Your partial response is preserved; please try again.");
      }else{
        const result=await response.json().catch(()=>({error:response.status===401||response.status===403 ? "Your session needs to reconnect. Sign in again to continue." : "Core is temporarily unavailable. Please try again."})) as CoreResponse & {error?:string};
        if(!response.ok || !result.responseId)throw new Error(result.error||"Core could not complete this request.");
        payload=result;
      }
      if (version !== generation.current) return null;
      setAttachment(null);
      setMessages(current => current.map(item => item.id === id ? { ...item, text: payload!.text, meta: payload!.attachmentSummary ?? undefined, references: payload!.references, pending: false, completedAt: Date.now() } : item));
      return payload;
    } catch (error) {
      if (version === generation.current) setMessages(current => current.map(item => item.id === id ? { ...item, failure: error instanceof SyntaxError ? "The response stream could not be read. Please try again." : error instanceof Error ? error.message : "Core could not complete this request.", pending: false, error: true, completedAt: Date.now() } : item));
      return null;
    } finally {
      if (version === generation.current) {
        controller.current = null;
        const doc = currentConversation.current;
        if (doc) {
          try {
            const saved = await conversationRequest<CoreConversation>(`/api/conversations/${doc.id}`);
            if (version === generation.current) {
              if (saved.messages.some(item => item.id === id)) applyConversation(saved);
              else {
                currentConversation.current = saved; setConversation(saved);
                setConversationError("This message could not be saved. Your draft is still available below; send it again to retry.");
              }
            }
          } catch (error) {
            if (version === generation.current) setConversationError(error instanceof Error ? error.message : "Your response is visible here, but its saved copy could not be verified. Reload history to check.");
          }
        }
        if (version === generation.current) { sendLock.current = false; setLocalStreaming(false); setSending(Boolean(currentConversation.current?.activeTurnId)); void refreshHistory(); }
      }
    }
  }

  return { messages, attachment, sending, recording, inputError, attach, toggleRecording, clear, stop, send,
    conversation, history, historyQuery, setHistoryQuery, historyLoading, historyError, historyBusyId, loadingConversation,
    conversationError, ready, stopping, open, rename, remove, refreshHistory, hasMore: Boolean(nextCursor),
    loadMore: () => nextCursor ? refreshHistory(nextCursor) : Promise.resolve(),
    retryConversation: () => { const id = retrySelection.current || currentConversation.current?.id; return id ? open(id, false) : Promise.resolve(false); },
  };
}
